import {
  IContextMenuItemRefreshRules,
  TDatabaseId,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HookTypes } from '../../models/category/HookTypes';
import CommandPrescript from '../../services/commands/CommandPrescript';
import CommandViewModel, {
  CommandName,
} from '../../services/commands/CommandViewModel';
import { CommandsNameKeyMap } from '../../services/commands/constants';
import { RefreshStrategy } from '../../services/commands/wrap/CategoryCommand';
import { ClientContext } from '../../services/context/IContextService';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';

interface CommandsStateEntity {
  readonly commands: CommandViewModel[];
  readonly pending?: CommandName[];
}

export type CommandsState = Record<UniqueId, CommandsStateEntity>;

const commandsSlice = createSlice({
  name: 'commands',
  initialState: {} as CommandsState,
  reducers: {
    get: pendingAction<CommandsState, { datasetId: UniqueId }>(),
    getSuccess: successAction<
      CommandsState,
      {
        datasetId: UniqueId;
        commands: CommandViewModel[];
        keyMap: CommandsNameKeyMap;
        pending?: CommandName[];
      }
    >((state, { payload: { datasetId, commands } }) => {
      state[datasetId] = { commands };
    }),
    getFailure: failureAction(),
    initiate: pendingAction<
      CommandsState,
      { datasetId: UniqueId; command: CommandName; hook?: HookTypes }
    >(),
    initiateSuccess: successAction<
      CommandsState,
      { datasetId: UniqueId; command: CommandName; hook?: HookTypes } & {
        prescript: CommandPrescript<any>;
        context: ClientContext;
      }
    >(),
    initiateFailure: failureAction(),
    initiateScheduled(
      state,
      {
        payload: { datasetId, pending },
      }: PayloadAction<{ datasetId: UniqueId; pending: CommandName[] }>
    ) {
      if (state[datasetId]) {
        state[datasetId].pending = pending;
      } else {
        state[datasetId] = { commands: [], pending };
      }
    },
    execute: pendingAction<
      CommandsState,
      {
        datasetId: UniqueId;
        command: CommandName;
        args: any;
        formId?: UniqueId;
        hook?: HookTypes;
      }
    >(),
    executeSuccess: successAction<
      CommandsState,
      {
        datasetId: UniqueId;
        command: CommandName;
        args: any;
        formId?: UniqueId;
        hook?: HookTypes;
      } & {
        isMidOfMultiStep?: true;
        result?: {
          inParams?: Record<string, any>;
          refreshRules?: IContextMenuItemRefreshRules;
          refresh?: RefreshStrategy;
          results?: Map<TDatabaseId, Record<string, any>>;
          [other: string]: any;
        };
      }
    >(),
    executeFailure: failureAction(),
    dispose(
      state,
      { payload: { datasetId } }: PayloadAction<{ datasetId: UniqueId }>
    ) {
      delete state[datasetId];
    },
  },
});

export const commandsReducer = commandsSlice.reducer;

export default commandsSlice.actions;
